import { useEffect } from 'react'
import { parseISOToDateTime } from '@calendly/datetime'
import { Badge } from '@calendly/ui/components/badge'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'

import { useI18n } from 'locales'
import { formatDate } from 'services/spot_time'
import { formatAmount } from 'utils/currency'
import { computeInvoiceTotals, computeLineItemAmountCents } from 'utils/invoice-helpers'

import { useInvoicePageData } from './InvoiceContext'
import InvoicePaymentForm from './InvoicePaymentForm'
import {
  PageContainer,
  ContentWrapper,
  LeftColumn,
  RightColumn,
  InvoiceHeader,
  InvoiceHeaderImageContainer,
  InvoiceHeaderImage,
  InvoiceHeaderImagePlaceholder,
  InvoiceHeaderRight,
  InvoiceNumberLabel,
  InvoiceLeftColumnContent,
  InvoicePaidWatermark,
  InvoicePaidStamp,
  InvoiceDetails,
  InvoiceDetailColumn,
  InvoiceDetailColumnRight,
  InvoiceDetailLabel,
  InvoiceDetailBodyLine,
  InvoiceDetailDateText,
  InvoiceDueDateBlock,
  InvoiceDetailDateSplitRow,
  InvoiceDetailDateSplitColumn,
  InvoiceDetailInlineLabel,
  InvoiceAmountDueValue,
  InvoiceLineItemsSection,
  InvoiceLineItemsTable,
  InvoiceLineItemsHeaderCellDescription,
  InvoiceLineItemsHeaderCellRight,
  InvoiceLineItemsRow,
  InvoiceLineItemsCellDescription,
  InvoiceLineItemsCell,
  InvoiceLineItemsCellRight,
  InvoiceLineItemName,
  InvoiceLineItemDescription,
  InvoiceLineItemMobileMeta,
  InvoiceLineItemMobileMetaItem,
  InvoiceTotalsSection,
  InvoiceTotalsRow,
  InvoiceTotalsRowDiscount,
  InvoiceTotalsRowTotal,
  InvoiceTotalsLabel,
  InvoiceTotalsValue,
  InvoiceNotesSection,
  InvoiceNotesLabel,
  InvoiceNotesBody,
} from './styles'

const isSafeHeaderImageUrl = (value) => {
  if (typeof value !== 'string') {
    return false
  }

  const normalizedValue = value.trim()
  if (!normalizedValue) {
    return false
  }

  try {
    return new URL(normalizedValue).protocol === 'https:'
  } catch {
    return false
  }
}

const InvoicePage = () => {
  const { invoice } = useInvoicePageData()
  const { i18n } = useI18n()
  const isPhone = useDeviceType('isPhone')

  const dateLargeFormat = i18n.t('time.formats.date_large')
  const createdAtFormatted = invoice?.created_at
    ? formatDate(
      parseISOToDateTime(invoice.created_at),
      dateLargeFormat,
      i18n.locale,
    )
    : ''
  const dueAtFormatted = invoice?.due_at
    ? formatDate(parseISOToDateTime(invoice.due_at), dateLargeFormat, i18n.locale)
    : ''

  const billerBusinessName = invoice?.invoice_biller_info?.business_name?.trim()
  const currency = invoice?.invoice_settings?.currency || 'USD'
  const lineItems = [...(invoice?.line_items || [])].sort(
    (a, b) => (a.position || 0) - (b.position || 0)
  )
  const {
    subtotalCents,
    discountTotalCents,
    taxTotalCents,
    totalCents,
    parsedTaxRate,
  } = computeInvoiceTotals(invoice)
  const taxName = invoice?.invoice_settings?.tax_name?.trim() || i18n.t('booking.invoice.tax')
  const amountDueValue = formatAmount(totalCents / 100, currency)
  const invoiceNotes = invoice?.notes?.trim()
  const headerImageSrc = isSafeHeaderImageUrl(invoice?.header_image_url)
    ? invoice.header_image_url
    : null
  const isPaid = invoice?.status?.toUpperCase() === 'PAID'

  return (
    <PageContainer>
      <ContentWrapper>
        <LeftColumn>
          {isPaid ? (
            <InvoicePaidWatermark aria-hidden data-testid="invoice-paid-watermark">
              <InvoicePaidStamp>{i18n.t('booking.invoice.paid')}</InvoicePaidStamp>
            </InvoicePaidWatermark>
          ) : null}
          <InvoiceLeftColumnContent>
            <InvoiceHeader>
              <InvoiceHeaderImageContainer>
                {headerImageSrc ? (
                  <InvoiceHeaderImage
                    src={headerImageSrc}
                    alt=""
                    width={131}
                    height={87}
                  />
                ) : (
                  <InvoiceHeaderImagePlaceholder />
                )}
              </InvoiceHeaderImageContainer>
              <InvoiceHeaderRight>
                {isPaid ? (
                  <Badge type="success" data-testid="invoice-paid-badge">
                    {i18n.t('booking.invoice.paid')}
                  </Badge>
                ) : null}
                <InvoiceNumberLabel>
                  {i18n.t('booking.invoice.invoice_number', {
                    invoice_number: invoice.invoice_number,
                  })}
                </InvoiceNumberLabel>
              </InvoiceHeaderRight>
            </InvoiceHeader>

            <InvoiceDetails>
              <InvoiceDetailColumn>
                <InvoiceDetailLabel>
                  {`${i18n.t('booking.invoice.billed_from')}:`}
                </InvoiceDetailLabel>
                {invoice?.invoice_biller_info?.name ? (
                  <InvoiceDetailBodyLine>
                    {invoice.invoice_biller_info.name}
                  </InvoiceDetailBodyLine>
                ) : null}
                {billerBusinessName ? (
                  <InvoiceDetailBodyLine>{billerBusinessName}</InvoiceDetailBodyLine>
                ) : null}
                {invoice?.invoice_biller_info?.email ? (
                  <InvoiceDetailBodyLine>
                    {invoice.invoice_biller_info.email}
                  </InvoiceDetailBodyLine>
                ) : null}
              </InvoiceDetailColumn>

              <InvoiceDetailColumn>
                <InvoiceDetailLabel>
                  {`${i18n.t('booking.invoice.billed_to')}:`}
                </InvoiceDetailLabel>
                {invoice?.contact?.full_name ? (
                  <InvoiceDetailBodyLine>{invoice.contact.full_name}</InvoiceDetailBodyLine>
                ) : null}
                {invoice?.contact?.primary_email ? (
                  <InvoiceDetailBodyLine>
                    {invoice.contact.primary_email}
                  </InvoiceDetailBodyLine>
                ) : null}
              </InvoiceDetailColumn>

              <InvoiceDetailColumn>
                {isPhone ? (
                  <InvoiceDetailDateSplitRow>
                    <InvoiceDetailDateSplitColumn>
                      <InvoiceDetailInlineLabel>
                        {`${i18n.t('booking.invoice.invoice_date')}:`}
                      </InvoiceDetailInlineLabel>
                      <InvoiceDetailDateText>{createdAtFormatted}</InvoiceDetailDateText>
                    </InvoiceDetailDateSplitColumn>
                    <InvoiceDetailDateSplitColumn>
                      <InvoiceDetailInlineLabel>
                        {`${i18n.t('booking.invoice.due_date')}:`}
                      </InvoiceDetailInlineLabel>
                      <InvoiceDetailDateText>{dueAtFormatted}</InvoiceDetailDateText>
                    </InvoiceDetailDateSplitColumn>
                  </InvoiceDetailDateSplitRow>
                ) : (
                  <>
                    <InvoiceDetailLabel>
                      {`${i18n.t('booking.invoice.invoice_date')}:`}
                    </InvoiceDetailLabel>
                    <InvoiceDetailDateText>{createdAtFormatted}</InvoiceDetailDateText>
                    <InvoiceDueDateBlock>
                      <InvoiceDetailLabel>
                        {`${i18n.t('booking.invoice.due_date')}:`}
                      </InvoiceDetailLabel>
                      <InvoiceDetailDateText>{dueAtFormatted}</InvoiceDetailDateText>
                    </InvoiceDueDateBlock>
                  </>
                )}
              </InvoiceDetailColumn>

              <InvoiceDetailColumnRight>
                <InvoiceDetailLabel>
                  {i18n.t('booking.invoice.amount_due_with_currency', {
                    currency: invoice?.invoice_settings?.currency ?? '',
                  })}
                </InvoiceDetailLabel>
                <InvoiceAmountDueValue>{amountDueValue}</InvoiceAmountDueValue>
              </InvoiceDetailColumnRight>
            </InvoiceDetails>

            <InvoiceLineItemsSection>
              <InvoiceLineItemsTable>
                <thead>
                  <tr>
                    <InvoiceLineItemsHeaderCellDescription>
                      {isPhone
                        ? i18n.t('booking.invoice.line_items')
                        : i18n.t('booking.invoice.th_description')}
                    </InvoiceLineItemsHeaderCellDescription>
                    {!isPhone ? (
                      <>
                        <InvoiceLineItemsHeaderCellRight>
                          {i18n.t('booking.invoice.th_qty')}
                        </InvoiceLineItemsHeaderCellRight>
                        <InvoiceLineItemsHeaderCellRight>
                          {i18n.t('booking.invoice.th_price')}
                        </InvoiceLineItemsHeaderCellRight>
                        <InvoiceLineItemsHeaderCellRight>
                          {i18n.t('booking.invoice.th_amount')}
                        </InvoiceLineItemsHeaderCellRight>
                      </>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length > 0 ? (
                    lineItems.map((lineItem) => {
                      const lineAmountCents = computeLineItemAmountCents(lineItem)
                      return (
                        <InvoiceLineItemsRow key={lineItem.uuid}>
                          <InvoiceLineItemsCellDescription>
                            <InvoiceLineItemName>{lineItem.name}</InvoiceLineItemName>
                            {lineItem.description ? (
                              <InvoiceLineItemDescription>
                                {lineItem.description}
                              </InvoiceLineItemDescription>
                            ) : null}
                            {isPhone ? (
                              <InvoiceLineItemMobileMeta>
                                <InvoiceLineItemMobileMetaItem>
                                  {`${i18n.t('booking.invoice.th_qty')}: ${lineItem.quantity}`}
                                </InvoiceLineItemMobileMetaItem>
                                <InvoiceLineItemMobileMetaItem>
                                  {`${i18n.t('booking.invoice.th_price')}: ${formatAmount(
                                    (lineItem.unit_price_cents || 0) / 100,
                                    currency,
                                  )}`}
                                </InvoiceLineItemMobileMetaItem>
                                <InvoiceLineItemMobileMetaItem>
                                  {`${i18n.t('booking.invoice.th_amount')}: ${formatAmount(
                                    lineAmountCents / 100,
                                    currency,
                                  )}`}
                                </InvoiceLineItemMobileMetaItem>
                              </InvoiceLineItemMobileMeta>
                            ) : null}
                          </InvoiceLineItemsCellDescription>
                          {!isPhone ? (
                            <>
                              <InvoiceLineItemsCellRight>
                                {lineItem.quantity}
                              </InvoiceLineItemsCellRight>
                              <InvoiceLineItemsCellRight>
                                {formatAmount((lineItem.unit_price_cents || 0) / 100, currency)}
                              </InvoiceLineItemsCellRight>
                              <InvoiceLineItemsCellRight>
                                {formatAmount(lineAmountCents / 100, currency)}
                              </InvoiceLineItemsCellRight>
                            </>
                          ) : null}
                        </InvoiceLineItemsRow>
                      )
                    })
                  ) : (
                    <InvoiceLineItemsRow>
                      <InvoiceLineItemsCell colSpan={isPhone ? 1 : 4}>
                        {i18n.t('booking.invoice.no_line_items')}
                      </InvoiceLineItemsCell>
                    </InvoiceLineItemsRow>
                  )}
                </tbody>
              </InvoiceLineItemsTable>

              <InvoiceTotalsSection>
                <InvoiceTotalsRow>
                  <InvoiceTotalsLabel>{i18n.t('booking.invoice.subtotal')}</InvoiceTotalsLabel>
                  <InvoiceTotalsValue>
                    {formatAmount(subtotalCents / 100, currency)}
                  </InvoiceTotalsValue>
                </InvoiceTotalsRow>
                {discountTotalCents > 0 ? (
                  <InvoiceTotalsRowDiscount>
                    <InvoiceTotalsLabel>{i18n.t('booking.invoice.discount')}</InvoiceTotalsLabel>
                    <InvoiceTotalsValue>
                      -
                      {formatAmount(discountTotalCents / 100, currency)}
                    </InvoiceTotalsValue>
                  </InvoiceTotalsRowDiscount>
                ) : null}
                <InvoiceTotalsRow>
                  <InvoiceTotalsLabel>
                    {i18n.t('booking.invoice.tax_with_rate', {
                      tax_name: taxName,
                      rate: parsedTaxRate.toFixed(2),
                    })}
                  </InvoiceTotalsLabel>
                  <InvoiceTotalsValue>
                    {formatAmount(taxTotalCents / 100, currency)}
                  </InvoiceTotalsValue>
                </InvoiceTotalsRow>
                <InvoiceTotalsRowTotal>
                  <InvoiceTotalsLabel>{i18n.t('booking.invoice.total')}</InvoiceTotalsLabel>
                  <InvoiceTotalsValue>{formatAmount(totalCents / 100, currency)}</InvoiceTotalsValue>
                </InvoiceTotalsRowTotal>
              </InvoiceTotalsSection>
              {invoiceNotes ? (
                <InvoiceNotesSection>
                  <InvoiceNotesLabel>{i18n.t('booking.invoice.notes')}</InvoiceNotesLabel>
                  <InvoiceNotesBody>{invoiceNotes}</InvoiceNotesBody>
                </InvoiceNotesSection>
              ) : null}
            </InvoiceLineItemsSection>
          </InvoiceLeftColumnContent>
        </LeftColumn>

        {!isPaid ? (
          <RightColumn>
            <InvoicePaymentForm />
          </RightColumn>
        ) : null}
      </ContentWrapper>
    </PageContainer>
  )
}

export default InvoicePage
